import React, { useEffect, useRef, useState } from 'react';
import { HiOutlineExternalLink, HiOutlineSwitchHorizontal } from 'react-icons/hi';
import { api } from '../services/api';
import NumberInput, { parseNumberInput } from './NumberInput';
import './PancakeSwapGuide.css';

const DEFAULT_USDT = '0x55d398326f99059fF775485246999027B3197955';
const DEFAULT_DFI = '0x2fCe06658Ff76Ee91DeE896aa47A8E1Dfe0D4F80';
const REFRESH_MS = 20000;

export function pancakeSwapUrl(config) {
  const input = String(config?.dfi_contract || DEFAULT_DFI).trim();
  const output = String(config?.usdt_contract || DEFAULT_USDT).trim();
  const chain = Number(config?.chain_id) === 97 ? 'bscTestnet' : 'bsc';
  const params = new URLSearchParams({
    chain,
    inputCurrency: input,
    outputCurrency: output,
  });
  return `https://pancakeswap.finance/swap?${params.toString()}`;
}

function formatRate(usdt) {
  const amount = Number(usdt);
  if (!Number.isFinite(amount) || amount <= 0) return '';
  return amount.toLocaleString(undefined, { maximumFractionDigits: 6 });
}

function trimAmount(value) {
  if (!Number.isFinite(value)) return '';
  const text = value.toFixed(8);
  return text.replace(/\.?0+$/, '') || '0';
}

function quoteRate(quote) {
  const rate = Number(quote?.usdt);
  return quote?.available && Number.isFinite(rate) && rate > 0 ? rate : 0;
}

export default function PancakeSwapGuide({ config, variant = 'panel' }) {
  const href = pancakeSwapUrl(config);
  const [quote, setQuote] = useState(null);
  const [dfi, setDfi] = useState('1');
  const [usdt, setUsdt] = useState('');
  const lastEdit = useRef('dfi');
  const dfiRef = useRef(dfi);
  const usdtRef = useRef(usdt);
  dfiRef.current = dfi;
  usdtRef.current = usdt;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await api.pancakeQuote();
        if (!cancelled) setQuote(data.quote || null);
      } catch {
        if (!cancelled) {
          setQuote((current) => current || { available: false });
        }
      }
    }
    load();
    const timer = window.setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  const rate = quoteRate(quote);

  useEffect(() => {
    if (!rate) return;
    if (lastEdit.current === 'usdt') {
      const amount = parseNumberInput(usdtRef.current);
      setDfi(Number.isFinite(amount) ? trimAmount(amount / rate) : '');
      return;
    }
    const amount = parseNumberInput(dfiRef.current);
    if (!dfiRef.current) {
      setDfi('1');
      setUsdt(trimAmount(rate));
      return;
    }
    if (!Number.isFinite(amount)) return;
    setUsdt(trimAmount(amount * rate));
  }, [rate]);

  function onDfiChange(value) {
    lastEdit.current = 'dfi';
    setDfi(value);
    const amount = parseNumberInput(value);
    if (!value || !Number.isFinite(amount) || !rate) {
      setUsdt('');
      return;
    }
    setUsdt(trimAmount(amount * rate));
  }

  function onUsdtChange(value) {
    lastEdit.current = 'usdt';
    setUsdt(value);
    const amount = parseNumberInput(value);
    if (!value || !Number.isFinite(amount) || !rate) {
      setDfi('');
      return;
    }
    setDfi(trimAmount(amount / rate));
  }

  const rateLabel = formatRate(quote?.usdt);
  const ready = Boolean(rate && rateLabel);

  return (
    <section className={`pcs-guide pcs-guide--${variant}`} aria-label="Swap DFI to USDT on PancakeSwap">
      <div className="pcs-guide__grid">
        <div className="pcs-guide__copy">
          <div className="pcs-guide__top">
            <span className="pcs-guide__icon" aria-hidden="true">
              <HiOutlineSwitchHorizontal />
            </span>
            <div>
              <h2>Swap DFI to USDT on PancakeSwap</h2>
              <p>
                After you claim, DFI sits in your wallet. Use PancakeSwap on BNB Smart Chain to sell that DFI for USDT.
              </p>
            </div>
          </div>
          <p className="pcs-guide__restrict">
            Buying DFI is restricted. Only selling DFI for USDT is available on PancakeSwap.
          </p>
          <ol className="pcs-guide__steps">
            <li>Open Sell and connect the same wallet you used here.</li>
            <li>Keep the pair as DFI → USDT. Sell opens that pair for you.</li>
            <li>Enter how much DFI to sell, check the quote, then tap Swap and confirm in your wallet.</li>
            <li>Keep a little BNB in the wallet for the network fee.</li>
          </ol>
          <div className="pcs-guide__actions">
            <button
              type="button"
              className="pcs-guide__btn pcs-guide__btn--buy"
              disabled
              title="Buying DFI is restricted"
            >
              Buy
            </button>
            <a className="pcs-guide__btn pcs-guide__btn--sell" href={href} target="_blank" rel="noreferrer">
              Sell
              <HiOutlineExternalLink />
            </a>
          </div>
        </div>

        <div className="pcs-guide__converter">
          <div className="pcs-guide__rate" aria-live="polite">
            <span className={`pcs-guide__live${ready ? ' is-on' : quote && quote.available === false ? ' is-off' : ' is-wait'}`}>
              <i className="pcs-guide__live-dot" aria-hidden="true" />
              Live rate
            </span>
            <strong>
              {ready
                ? `1 DFI ≈ ${rateLabel} USDT`
                : quote && quote.available === false
                  ? 'Rate unavailable'
                  : 'Fetching PancakeSwap rate…'}
            </strong>
            <em>Type DFI or USDT to convert. Estimate only — the swap can differ.</em>
          </div>
          <label className="pcs-guide__field">
            DFI
            <NumberInput
              id="pcs-dfi"
              value={dfi}
              onChange={onDfiChange}
              allowDecimal
              maxDecimals={8}
              placeholder="0.00"
              disabled={!ready}
            />
          </label>
          <span className="pcs-guide__swap-ico" aria-hidden="true">
            <HiOutlineSwitchHorizontal />
          </span>
          <label className="pcs-guide__field">
            USDT
            <NumberInput
              id="pcs-usdt"
              value={usdt}
              onChange={onUsdtChange}
              allowDecimal
              maxDecimals={8}
              placeholder="0.00"
              disabled={!ready}
            />
          </label>
        </div>
      </div>
    </section>
  );
}
